import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import LoginPage from './components/auth/LoginPage';
import OtpVerificationPage from './components/auth/OtpVerificationPage';
import ForcePasswordChangePage from './components/auth/ForcePasswordChangePage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import ChangePasswordPage from './components/auth/ChangePasswordPage';
import { Dashboard } from './components/dashboard/Dashboard';
import Layout from './components/layout/Layout';
import TransactionLookupPage from './components/pay/TransactionLookupPage';
import TransactionApprovalPage from './components/approval/TransactionApprovalPage';
import TransactionHistoryPage from './components/history/TransactionHistoryPage';
import TransactionApprovalHistoryPage from './components/maker/TransactionApprovalHistoryPage';
import NewTransactionPage from './components/pay/NewTransactionPage';
import FloatBalancePage from './components/management/FloatBalancePage';
import AgentManagementPage from './components/management/AgentManagementPage';
import NotificationsPage from './components/reports/NotificationsPage';
import ReportsPage from './components/reports/ReportsPage';
import SupportPage from './components/support/SupportPage';
import BranchUsersPage from './components/users/BranchUsersPage';
import AuditLogPage from './components/audit/AuditLogPage';
import RejectedTransactionsPage from './components/maker/RejectedTransactionsPage';
import MakerApprovalHistoryPage from './components/maker/MakerApprovalHistoryPage';
import RejectedTransactionsApprovalPage from './components/maker/RejectedTransactionsApprovalPage';
import EPRCGenerationPage from './components/eprc/EPRCGenerationPage';
import { AuthProvider } from './context/AuthContext';
import { useTransactionStatusUpdater } from './hooks/useTransactionStatusUpdater';
const theme = createTheme({
  palette: {
    primary: {
      main: '#0052CC',
      light: '#E8F0FE',
      dark: '#003DA8'
    },
    secondary: {
      main: '#6B7280',
      light: '#9CA3AF',
      dark: '#4B5563'
    },
    error: {
      main: '#AE2A19'
    },
    warning: {
      main: '#B38600'
    },
    info: {
      main: '#0052CC'
    },
    success: {
      main: '#0B7923'
    },
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF'
    },
    text: {
      primary: '#1F2937',
      secondary: '#6B7280'
    }
  },
  typography: {
    fontFamily: '"Inter", sans-serif',
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: '-0.01em'
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 700,
      letterSpacing: '-0.01em'
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
      letterSpacing: '-0.01em'
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 600
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 600
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.02em'
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 6,
          padding: '10px 20px',
          fontWeight: 600,
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0px 1px 3px rgba(0,0,0,0.12)'
          },
          '&:active': {
            boxShadow: 'none'
          }
        },
        containedPrimary: {
          background: '#0052CC',
          boxShadow: 'none',
          '&:hover': {
            background: '#003DA8'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
          border: '1px solid #E5E7EB',
          transition: 'box-shadow 0.2s ease-in-out'
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            transition: 'all 0.2s ease-in-out',
            '&.Mui-focused': {
              boxShadow: 'none'
            },
            '&:hover': {
              boxShadow: 'none'
            }
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.95rem',
            fontWeight: 500
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0052CC',
            borderWidth: 2
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#9CA3AF'
          }
        },
        input: {
          padding: '10px 12px'
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: '#0052CC'
          }
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px'
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.05)'
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 82, 204, 0.3)',
            borderRadius: '4px'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(0, 82, 204, 0.5)'
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          boxShadow: 'none'
        },
        standardSuccess: {
          backgroundColor: '#ECFDF5',
          border: '1px solid #0B7923',
          color: '#047857',
          '& .MuiAlert-icon': {
            color: '#0B7923'
          }
        },
        standardError: {
          backgroundColor: '#FEF2F2',
          border: '1px solid #AE2A19',
          color: '#991B1B',
          '& .MuiAlert-icon': {
            color: '#AE2A19'
          }
        },
        standardWarning: {
          backgroundColor: '#FFFBEB',
          border: '1px solid #B38600',
          color: '#92400E',
          '& .MuiAlert-icon': {
            color: '#B38600'
          }
        },
        standardInfo: {
          backgroundColor: '#EFF6FF',
          border: '1px solid #0052CC',
          color: '#0052CC',
          '& .MuiAlert-icon': {
            color: '#0052CC'
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(0, 82, 204, 0.1)'
          }
        }
      }
    }
  },
  shape: {
    borderRadius: 8
  },
  spacing: 8
});
export function App() {
  useTransactionStatusUpdater();
  return <AuthProvider data-id="element-0">
      <ThemeProvider theme={theme} data-id="element-1">
        <CssBaseline data-id="element-2" />
        <Router data-id="element-3">
          <Routes data-id="element-4">
            <Route path="/login" element={<LoginPage data-id="element-6" />} data-id="element-5" />
            <Route path="/otp-verification" element={<OtpVerificationPage data-id="element-8" />} data-id="element-7" />
            <Route path="/force-password-change" element={<ForcePasswordChangePage data-id="element-10" />} data-id="element-9" />
            <Route path="/forgot-password" element={<ForgotPasswordPage data-id="element-12" />} data-id="element-11" />
            <Route path="/reset-password" element={<ResetPasswordPage data-id="element-14" />} data-id="element-13" />
            <Route path="/change-password" element={<ChangePasswordPage data-id="element-16" />} data-id="element-15" />
            <Route path="/" element={<Layout data-id="element-18" />} data-id="element-17">
              <Route index element={<Dashboard data-id="element-20" />} data-id="element-19" />
              <Route path="float-balance" element={<FloatBalancePage data-id="element-22" />} data-id="element-21" />
              <Route path="transaction-lookup" element={<TransactionLookupPage data-id="element-24" />} data-id="element-23" />
              <Route path="rejected-transactions" element={<RejectedTransactionsPage data-id="element-26" />} data-id="element-25" />
              <Route path="rejected-transactions-approval" element={<RejectedTransactionsApprovalPage data-id="element-28" />} data-id="element-27" />
              <Route path="maker-approval-history" element={<MakerApprovalHistoryPage data-id="element-30" />} data-id="element-29" />
              <Route path="transaction-approval" element={<TransactionApprovalPage data-id="element-32" />} data-id="element-31" />
              <Route path="history" element={<TransactionHistoryPage data-id="element-34" />} data-id="element-33" />
              <Route path="transaction-approval-history" element={<TransactionApprovalHistoryPage data-id="element-36" />} data-id="element-35" />
              <Route path="branch-users" element={<BranchUsersPage data-id="element-38" />} data-id="element-37" />
              <Route path="audit-log" element={<AuditLogPage data-id="element-40" />} data-id="element-39" />
              <Route path="notifications" element={<NotificationsPage data-id="element-42" />} data-id="element-41" />
              <Route path="reports" element={<ReportsPage data-id="element-44" />} data-id="element-43" />
              <Route path="support" element={<SupportPage data-id="element-46" />} data-id="element-45" />
            </Route>
            <Route path="*" element={<Navigate to="/login" replace data-id="element-48" />} data-id="element-47" />
          </Routes>
        </Router>
      </ThemeProvider>
    </AuthProvider>;
}